import { cn } from "./cn";

/*
 * Rounded "forward" arrow used as the social-block affordance, taken 1:1 from
 * the reference preview ArrowIcon (icons/preview/arrow). Kept inline so it
 * matches the social row exactly; `rtl:rotate-180` mirrors it for RTL.
 */
const SocialArrowIcon = () => (
  <svg
    className="rtl:rotate-180"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M4 11.7256C4 11.3459 4.28215 11.0321 4.64823 10.9824L4.75 10.9756H19.75C20.1642 10.9756 20.5 11.3114 20.5 11.7256C20.5 12.1053 20.2178 12.4191 19.8518 12.4687L19.75 12.4756L4.75 12.4756C4.33579 12.4756 4 12.1398 4 11.7256Z"
      fill="#767c83"
    />
    <path
      d="M13.171 6.23271C12.8775 5.94045 12.8765 5.46558 13.1687 5.17206C13.4344 4.90522 13.851 4.88012 14.1451 5.09734L14.2294 5.16977L20.2794 11.1938C20.547 11.4603 20.5714 11.8784 20.3524 12.1724L20.2794 12.2567L14.2294 18.2817C13.9359 18.574 13.4611 18.573 13.1688 18.2795C12.9031 18.0127 12.8797 17.5959 13.0982 17.3028L13.171 17.2188L18.687 11.7249L13.171 6.23271Z"
      fill="#767c83"
    />
  </svg>
);

/*
 * One social account row. Matches the shared SocialNetWorks used by Business,
 * Menu, Vcard and the rest: a 44px icon, the platform name, an optional
 * description subline (rendered only when `description` is set), and the
 * forward arrow. `href` makes a filled account open in a new tab.
 */
const SocialBlock = ({ icon, title, description, href, textFont }) => {
  const inner = (
    <div className="flex flex-row items-center justify-between p-[10px]">
      <div className="flex flex-row items-center space-x-[8px] xs:space-x-[12px] sm:space-x-[20px]">
        <img src={icon} className="w-[44px] rtl:ml-2" alt={title} />
        <div className="flex flex-col justify-center text-left">
          <p
            className="text-sm xs:text-base sm:text-xl font-medium text-event-900 break-words rtl:text-base rtl:leading-4 rtl:text-right"
            style={textFont ? { fontFamily: textFont } : undefined}
          >
            {title}
          </p>
          {description && (
            <p
              className="text-[10px] xs:text-xs text-event-500 break-words rtl:text-right"
              style={textFont ? { fontFamily: textFont } : undefined}
            >
              {description}
            </p>
          )}
        </div>
      </div>
      {/* ms-2 is the space between the text and the arrow. Do not change. */}
      <span className="ms-2 w-6">
        <SocialArrowIcon />
      </span>
    </div>
  );

  const className = cn(
    "flex w-full cursor-pointer flex-col break-all rounded bg-white shadow-[1px_6px_30px_rgba(0,31,14,0.05)]",
    href && "hover:opacity-90",
  );

  return href ? (
    <a href={href} target="_blank" rel="noopener noreferrer" className={className}>
      {inner}
    </a>
  ) : (
    <div className={className}>{inner}</div>
  );
};

/*
 * "Our Social Networks" section. Mirrors EventSocialNetworks in the reference:
 * a left-aligned title above a stack of social blocks. Renders nothing when no
 * social accounts are present (live mode only).
 */
const EventSocialNetworks = ({ title, blocks, titleFont, textFont }) => {
  if (!blocks || blocks.length === 0) return null;

  return (
    <div className="flex w-full flex-col items-start justify-center" id="socialBlock">
      {/*
       * Title font drives the section heading (OQG3-2381 / OQG3-2562). Platform
       * names and sublines stay on the Texts font so body copy is unaffected.
       */}
      <p
        className="mb-2.5 text-lg xs:text-xl font-medium text-event-900"
        style={titleFont ? { fontFamily: titleFont } : undefined}
      >
        {title}
      </p>
      <div className="w-full space-y-[8px] xs:space-y-[15px]">
        {blocks.map((block, index) => (
          <SocialBlock
            key={`event_social_${index}_${block.title}`}
            icon={block.icon}
            title={block.title}
            description={block.description}
            href={block.href}
            textFont={textFont}
          />
        ))}
      </div>
    </div>
  );
};

export default EventSocialNetworks;
